import React from 'react';
import { X } from 'lucide-react';
import { DataTableColumn, FilterRule } from '../../../types/grid';
import { OperatorDef, getColumnName, getOperatorSymbol } from './types';
import { Button } from '../../ui/Button';

interface FilterSearchBadgesProps<T extends Record<string, any>> {
  columns: DataTableColumn<T>[];
  filterRules: FilterRule[];
  searchValue: string;
  pendingColumn: DataTableColumn<T> | null;
  pendingOperator: OperatorDef | null;
  onRemoveFilterRule: (ruleId: string) => void;
  onRemoveSearchText: () => void;
  onResetPendingState: () => void;
  onSelectSearchText: (text: string) => void;
}

export function FilterSearchBadges<T extends Record<string, any>>({
  columns,
  filterRules,
  searchValue,
  pendingColumn,
  pendingOperator,
  onRemoveFilterRule,
  onRemoveSearchText,
  onResetPendingState,
  onSelectSearchText,
}: FilterSearchBadgesProps<T>) {
  return (
    <>
      {/* Committed Structured Filter Badges */}
      {filterRules.map((rule) => {
        const col = columns.find((c) => c.id === rule.columnKey);
        const colName = col ? getColumnName(col) : rule.columnKey.replace(/^data_/, '');
        const symbol = getOperatorSymbol(rule);
        const isBool = rule.operator === 'boolean';
        const isNoVal =
          rule.operator === 'is_empty' ||
          rule.operator === 'is_not_empty';

        return (
          <div
            key={rule.id}
            className="inline-flex items-center h-6 bg-zinc-200/80 dark:bg-zinc-700/80 border border-zinc-300/80 dark:border-zinc-600/80 rounded overflow-hidden text-xs shrink-0 select-none animate-in fade-in-50 duration-150"
            onClick={(e) => e.stopPropagation()}
          >
            <span className="px-2 py-0.5 text-zinc-800 dark:text-zinc-200 font-medium">
              {colName}
            </span>
            <span className="px-1.5 py-0.5 font-sans font-medium text-zinc-600 dark:text-zinc-300 border-l border-zinc-300/80 dark:border-zinc-600/80">
              {symbol}
            </span>
            {!isBool && !isNoVal && (
              <span className="px-2 py-0.5 text-zinc-900 dark:text-zinc-100 font-medium border-l border-zinc-300/80 dark:border-zinc-600/80 max-w-[160px] truncate">
                {rule.value}
              </span>
            )}
            <Button
              type="button"
              variant="ghost"
              size="icon-xs"
              onClick={() => onRemoveFilterRule(rule.id)}
              className="h-full w-5 rounded-none px-1 text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-100 hover:bg-black/5 dark:hover:bg-white/10 border-l border-zinc-300/60 dark:border-zinc-600/60"
              title="Remove filter"
            >
              <X size={11} />
            </Button>
          </div>
        );
      })}

      {/* Committed Freeform Text Search Badge */}
      {searchValue && (
        <div
          className="inline-flex items-center h-6 bg-zinc-200/80 dark:bg-zinc-700/80 border border-zinc-300/80 dark:border-zinc-600/80 rounded px-2 py-0.5 text-xs shrink-0 select-none gap-1.5 animate-in fade-in-50 duration-150"
          onClick={(e) => {
            e.stopPropagation();
            onSelectSearchText(searchValue);
          }}
        >
          <span className="text-zinc-800 dark:text-zinc-200 font-medium max-w-[180px] truncate">
            {searchValue}
          </span>
          <Button
            type="button"
            variant="ghost"
            size="icon-xs"
            onClick={(e) => {
              e.stopPropagation();
              onRemoveSearchText();
            }}
            className="h-4 w-4 text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-100"
            title="Clear text search"
          >
            <X size={11} />
          </Button>
        </div>
      )}

      {/* In-Progress / Pending Category Pill */}
      {pendingColumn && (
        <div
          className="inline-flex items-center h-6 bg-zinc-200/90 dark:bg-zinc-700/90 border border-zinc-300/80 dark:border-zinc-600/80 rounded overflow-hidden text-xs shrink-0 select-none"
          onClick={(e) => e.stopPropagation()}
        >
          <span className="px-2 py-0.5 text-zinc-800 dark:text-zinc-200 font-medium">
            {getColumnName(pendingColumn)}
          </span>
          {pendingOperator && (
            <span className="px-1.5 py-0.5 font-sans font-medium text-zinc-600 dark:text-zinc-300 border-l border-zinc-300/80 dark:border-zinc-600/80">
              {pendingOperator.symbol}
            </span>
          )}
          <Button
            type="button"
            variant="ghost"
            size="icon-xs"
            onClick={onResetPendingState}
            className="h-full w-5 rounded-none px-1 text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-100 hover:bg-black/5 dark:hover:bg-white/10 border-l border-zinc-300/60 dark:border-zinc-600/60"
            title="Cancel condition"
          >
            <X size={11} />
          </Button>
        </div>
      )}
    </>
  );
}
